import { useCallback, useEffect, useMemo, useState } from "react";
import ToastHelper from "@/utils/toastHelper";
import { CheckCircle2, Download, FileText, Loader2, Upload, X } from "lucide-react";
import {
  STUDENT_DOCUMENT_ACCEPT,
  STUDENT_DOCUMENT_DEFINITIONS,
  STUDENT_DOCUMENT_MAX_BYTES,
} from "@/constants/studentDocuments";
import { studentDocumentsService } from "@/services/studentDocumentsService";

const EMPTY_UPLOAD = {
  issueDate: "",
  expiryDate: "",
  remarks: "",
  documentDescription: "",
  file: null,
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value) => value
  ? new Intl.DateTimeFormat("en-AU", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
  : "";

export default function StudentDocuments() {
  const [apiDocuments, setApiDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [uploadValues, setUploadValues] = useState(EMPTY_UPLOAD);
  const [validationMessage, setValidationMessage] = useState("");
  const [uploadingTypeId, setUploadingTypeId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadDocuments = useCallback(async () => {
    try {
      setLoadError("");
      const data = await studentDocumentsService.getDocuments();
      setApiDocuments(Array.isArray(data) ? data : []);
    } catch (error) {
      setLoadError(getErrorMessage(error, "Unable to load your documents."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const documents = useMemo(() => STUDENT_DOCUMENT_DEFINITIONS.map((definition) => ({
    ...definition,
    ...(apiDocuments.find((document) => Number(document.studentDocTypeId) === definition.id) || {}),
  })), [apiDocuments]);

  const openUpload = (document) => {
    setEditingTypeId(document.id);
    setValidationMessage("");
    setUploadValues({
      ...EMPTY_UPLOAD,
      issueDate: document.issueDate?.slice(0, 10) || "",
      expiryDate: document.expiryDate?.slice(0, 10) || "",
    });
  };

  const cancelUpload = () => {
    if (uploadingTypeId) return;
    setEditingTypeId(null);
    setUploadValues(EMPTY_UPLOAD);
    setValidationMessage("");
  };

  const selectFile = (event) => {
    const file = event.target.files?.[0] || null;
    setValidationMessage("");
    if (!file) return setUploadValues((current) => ({ ...current, file: null }));
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!["jpg", "jpeg", "png", "pdf"].includes(extension)) {
      event.target.value = "";
      return setValidationMessage("Only JPG, JPEG, PNG and PDF files are allowed.");
    }
    if (file.size > STUDENT_DOCUMENT_MAX_BYTES) {
      event.target.value = "";
      return setValidationMessage("The selected file exceeds the 10 MB size limit.");
    }
    setUploadValues((current) => ({ ...current, file }));
  };

  const submitUpload = async (event) => {
    event.preventDefault();
    if (!uploadValues.file) return setValidationMessage("Please select a file to upload.");
    if (uploadValues.issueDate && uploadValues.expiryDate && uploadValues.expiryDate < uploadValues.issueDate) {
      return setValidationMessage("Expiry date cannot be earlier than issue date.");
    }

    setValidationMessage("");
    setUploadingTypeId(editingTypeId);
    setUploadProgress(0);
    try {
      const response = await studentDocumentsService.upload({
        ...uploadValues,
        studentDocTypeId: editingTypeId,
      }, (progressEvent) => {
        if (progressEvent.total) setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      });
      ToastHelper.success(response.message || "Document uploaded successfully.");
      setEditingTypeId(null);
      setUploadValues(EMPTY_UPLOAD);
      await loadDocuments();
    } catch (error) {
      setValidationMessage(getErrorMessage(error, "Document upload failed."));
    } finally {
      setUploadingTypeId(null);
      setUploadProgress(0);
    }
  };

  const download = async (document) => {
    setDownloadingId(document.studentDocId);
    try {
      const response = await studentDocumentsService.download(document.studentDocId);
      const url = window.URL.createObjectURL(response.data);
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = document.fileName || "document";
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      ToastHelper.error(getErrorMessage(error, "Unable to download this document."));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="documents-list">
      <div>
        <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">My documents</h1>
        <p className="text-sm text-slate-500 mt-1">Upload your identity and supporting documents securely.</p>
      </div>

      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
          {loadError} <button className="underline font-semibold ml-1" onClick={loadDocuments}>Try again</button>
        </div>
      )}

      {loading ? (
        <div className="wc-card flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading documents...
        </div>
      ) : (
        <div className="grid gap-3">
          {documents.map((document) => {
            const uploaded = Boolean(document.uploaded);
            const canUpload = Boolean(document.canUploadDocuments);
            const isUploading = uploadingTypeId === document.id;
            return (
              <div key={document.id} className="wc-card" data-testid={`doc-${document.key}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className={`w-11 h-11 rounded-lg flex shrink-0 items-center justify-center ${uploaded ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                    {uploaded ? <CheckCircle2 className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0A2540]">{document.studentDocName || document.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {uploaded ? `${document.fileName} · ${formatBytes(document.fileSize)}` : "Not yet uploaded"}
                    </div>
                    {uploaded && document.uploadedDate && <div className="text-xs text-slate-400 mt-0.5">Uploaded {formatDate(document.uploadedDate)}</div>}
                  </div>
                  <div className="flex gap-2">
                    {uploaded && (
                      <button onClick={() => download(document)} disabled={downloadingId === document.studentDocId} className="wc-btn-outline text-xs" data-testid={`download-${document.key}`}>
                        {downloadingId === document.studentDocId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download
                      </button>
                    )}
                    {canUpload && (
                      <button onClick={() => openUpload(document)} disabled={Boolean(uploadingTypeId)} className={uploaded ? "wc-btn-outline text-xs" : "wc-btn-primary text-xs"} data-testid={`upload-${document.key}`}>
                        <Upload className="w-3.5 h-3.5" /> {uploaded ? "Replace" : "Upload"}
                      </button>
                    )}
                  </div>
                </div>

                {editingTypeId === document.id && (
                  <form onSubmit={submitUpload} className="mt-4 border-t border-slate-100 pt-4 space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="text-xs font-semibold text-slate-600">Issue date
                        <input type="date" className="wc-input mt-1 w-full" value={uploadValues.issueDate} onChange={(event) => setUploadValues((current) => ({ ...current, issueDate: event.target.value }))} />
                      </label>
                      <label className="text-xs font-semibold text-slate-600">Expiry date
                        <input type="date" className="wc-input mt-1 w-full" min={uploadValues.issueDate || undefined} value={uploadValues.expiryDate} onChange={(event) => setUploadValues((current) => ({ ...current, expiryDate: event.target.value }))} />
                      </label>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="text-xs font-semibold text-slate-600">Remarks
                        <input className="wc-input mt-1 w-full" value={uploadValues.remarks} onChange={(event) => setUploadValues((current) => ({ ...current, remarks: event.target.value }))} />
                      </label>
                      <label className="text-xs font-semibold text-slate-600">Document description
                        <input className="wc-input mt-1 w-full" value={uploadValues.documentDescription} onChange={(event) => setUploadValues((current) => ({ ...current, documentDescription: event.target.value }))} />
                      </label>
                    </div>
                    <label className="block text-xs font-semibold text-slate-600">File <span className="text-red-500">*</span>
                      <input type="file" required accept={STUDENT_DOCUMENT_ACCEPT} onChange={selectFile} className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-[#0A2540]" />
                    </label>
                    <p className="text-xs text-slate-400">JPG, JPEG, PNG or PDF. Maximum 10 MB.</p>
                    {validationMessage && <div className="text-sm text-red-600" role="alert">{validationMessage}</div>}
                    {isUploading && <div className="text-xs text-slate-500">Uploading{uploadProgress ? ` ${uploadProgress}%` : "..."}</div>}
                    <div className="flex justify-end gap-2">
                      <button type="button" className="wc-btn-outline text-xs" disabled={isUploading} onClick={cancelUpload}><X className="w-3.5 h-3.5" /> Cancel</button>
                      <button type="submit" className="wc-btn-primary text-xs" disabled={isUploading}>
                        {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {uploaded ? "Replace document" : "Upload document"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            );
          })}
        </div>
      )}
      {!loading && documents.length > 0 && !documents.some((document) => document.canUploadDocuments) && (
        <p className="text-sm text-slate-500">Document uploads are available when your enrolment status is Documents Pending.</p>
      )}
    </div>
  );
}
